import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { useEffect } from 'react';

// Scroll to top on route change
function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HomePage from './pages/HomePage';
import DirectoryPage from './pages/DirectoryPage';
import MapPage from './pages/MapPage';
import ResourceDetailPage from './pages/ResourceDetailPage';
import SubmitResourcePage from './pages/SubmitResourcePage';
import ApplyMembershipPage from './pages/ApplyMembershipPage';
import AdminDashboard from './pages/AdminDashboard';
import AuthCallback from './pages/AuthCallback';
import ProfilePage from './pages/ProfilePage';
import MembersPage from './pages/MembersPage';
import MemberProfilePage from './pages/MemberProfilePage';
import NewsResourcesPage from './pages/NewsResourcesPage';
import ImpactDashboard from './pages/ImpactDashboard';
import DigitalBinderPage from './pages/DigitalBinderPage';
import SharedBinderPage from './pages/SharedBinderPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import TermsAcceptancePage from './pages/TermsAcceptancePage';
import NominatePartnerPage from './pages/NominatePartnerPage';
import AddOrganizationPage from './pages/AddOrganizationPage';
import AddResourcePage from './pages/AddResourcePage';
import WelcomePage from './pages/WelcomePage';
import SubmitMetricsPage from './pages/SubmitMetricsPage';
import ChapterDirectorDashboard from './pages/ChapterDirectorDashboard';
import PendingApprovalPage from './pages/PendingApprovalPage';
import LoginPage from './pages/LoginPage';
import EditResourcePage from './pages/EditResourcePage';
import GettingStartedPage from './pages/GettingStartedPage';
import MySubmissionsPage from './pages/MySubmissionsPage';
import PartnerDatabasesPage from './pages/PartnerDatabasesPage';

// Router component to handle auth callback
function AppRouter() {
    const location = useLocation();
    const { user, isAuthenticated, loading } = useAuth();
    
    // Check URL fragment for session_id (OAuth callback)
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    // If authenticated but terms not accepted, force terms acceptance
    const termsExemptPaths = ['/terms', '/terms/accept'];
    const isTermsExempt = termsExemptPaths.some(p => location.pathname.startsWith(p)) || location.pathname.startsWith('/shared/');
    
    if (!loading && isAuthenticated && user && !user.terms_accepted && !isTermsExempt) {
        const redirect = location.pathname !== '/' ? location.pathname : '/directory';
        return <Navigate to={`/terms/accept?redirect=${encodeURIComponent(redirect)}`} replace />;
    }

    // If authenticated, terms accepted, but NOT approved — show pending approval
    // Allow public pages (/, /directory public view, /apply, /submit, /map, /resource/*, /shared/*, /terms/*)
    const publicPaths = ['/', '/apply', '/map', '/impact', '/terms', '/shared/', '/login', '/getting-started'];
    const isPublicPath = publicPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/')) 
        || location.pathname.startsWith('/resource/') || location.pathname.startsWith('/shared/');
    
    if (!loading && isAuthenticated && user && user.terms_accepted && !user.approved && !user.is_admin && !isPublicPath) {
        return <PendingApprovalPage />;
    }
    
    return (
        <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-1">
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/directory" element={<DirectoryPage />} />
                    <Route path="/map" element={<MapPage />} />
                    <Route path="/impact" element={<ImpactDashboard />} />
                    <Route path="/resource/:id" element={<ResourceDetailPage />} />
                    <Route path="/submit" element={<SubmitResourcePage />} />
                    <Route path="/apply" element={<ApplyMembershipPage />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/members" element={<MembersPage />} />
                    <Route path="/members/:userId" element={<MemberProfilePage />} />
                    <Route path="/news" element={<NewsResourcesPage />} />
                    <Route path="/binder" element={<DigitalBinderPage />} />
                    <Route path="/shared/:shareId" element={<SharedBinderPage />} />
                    <Route path="/terms" element={<TermsConditionsPage />} />
                    <Route path="/terms/accept" element={<TermsAcceptancePage />} />
                    <Route path="/nominate" element={<NominatePartnerPage />} />
                    <Route path="/add-organization" element={<AddOrganizationPage />} />
                    <Route path="/add-resource" element={<AddResourcePage />} />
                    <Route path="/welcome" element={<WelcomePage />} />
                    <Route path="/submit-metrics" element={<SubmitMetricsPage />} />
                    <Route path="/chapter-dashboard" element={<ChapterDirectorDashboard />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/organizations/:id/edit" element={<EditResourcePage />} />
                    <Route path="/getting-started" element={<GettingStartedPage />} />
                    <Route path="/my-submissions" element={<MySubmissionsPage />} />
                    <Route path="/partners" element={<PartnerDatabasesPage />} />
                </Routes>
            </main>
            <Footer />
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <ScrollToTop />
            <AuthProvider>
                <AppRouter />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
